import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  RefreshCw, RotateCcw, FileText, DollarSign, AlertCircle, Loader2, 
  CheckCircle, XCircle, History, GitBranch, TrendingUp, TrendingDown,
  Eye, Edit3, ArrowRight, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import { cn } from '@/lib/utils';

// ═══════════════════════════════════════════════════════════════════════════════
// BADGE CONFIGURATIONS
// ═══════════════════════════════════════════════════════════════════════════════

const INVOICE_STATUS_BADGE = {
  draft: { label: 'Draft', className: 'bg-gray-500 text-white' },
  GENERATED: { label: 'Generated', className: 'bg-blue-500 text-white' },
  needs_regeneration: { label: 'Needs Regeneration', className: 'bg-orange-500 text-white' },
  superseded: { label: 'Superseded', className: 'bg-gray-400 text-white' },
  SUBMITTED: { label: 'Submitted', className: 'bg-purple-600 text-white' },
  PAID: { label: 'Paid', className: 'bg-green-600 text-white' },
  adjusted: { label: 'Adjusted', className: 'bg-yellow-600 text-white' },
};

const ADJUSTMENT_STATUS_BADGE = {
  pending_review: { label: 'Pending Review', className: 'bg-orange-500 text-white' },
  approved: { label: 'Approved', className: 'bg-green-600 text-white' },
  rejected: { label: 'Rejected', className: 'bg-red-600 text-white' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);
};

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  try {
    return new Date(isoString).toLocaleString('en-AU', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit' 
    });
  } catch {
    return isoString;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// BADGE COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const InvoiceStatusBadge = ({ status }) => {
  const config = INVOICE_STATUS_BADGE[status] || { label: status || '-', className: 'bg-gray-500 text-white' };
  return (
    <Badge className={`${config.className} text-xs`} data-testid="invoice-status-badge">
      {config.label}
    </Badge>
  );
};

const AdjustmentStatusBadge = ({ status }) => {
  const config = ADJUSTMENT_STATUS_BADGE[status] || { label: status || '-', className: 'bg-gray-500 text-white' };
  return (
    <Badge className={`${config.className} text-xs`} data-testid="adjustment-status-badge">
      {config.label}
    </Badge>
  );
};

const AmountDifferenceBadge = ({ amount }) => {
  if (!amount || amount === 0) {
    return <span className="text-muted-foreground">-</span>;
  }
  const isPositive = amount > 0;
  return (
    <Badge className={cn(
      "text-xs flex items-center gap-1",
      isPositive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
    )}>
      {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {isPositive ? '+' : ''}{formatCurrency(amount)}
    </Badge>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// VERSION HISTORY DRAWER
// ═══════════════════════════════════════════════════════════════════════════════

const InvoiceVersionHistoryDrawer = ({ invoiceId, open, onClose }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !invoiceId) return;
    
    const fetchVersions = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/invoice-regen/invoices/${invoiceId}/versions`);
        setVersions(res.data.data || []);
      } catch (e) {
        notify.error(e, 'INVOICE_REGEN_LOAD_FAILED');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVersions();
  }, [open, invoiceId]);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="invoice-version-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Invoice Version History
          </SheetTitle>
          <SheetDescription>
            All versions of this invoice including superseded
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : versions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No version history found</p>
          ) : (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
              
              {versions.map((version, idx) => (
                <div key={version.id} className="relative pl-10 pb-6" data-testid={`version-${version.version || 1}`}>
                  <div className={cn(
                    "absolute left-2.5 w-3 h-3 rounded-full border-2",
                    version.is_superseded ? "bg-gray-300 border-gray-400" : 
                    version.is_regenerated ? "bg-purple-500 border-purple-600" : 
                    "bg-green-500 border-green-600"
                  )} />
                  
                  <Card className={cn(version.is_superseded && "opacity-60")}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2">
                          {version.invoice_number}
                          <InvoiceStatusBadge status={version.status} />
                        </CardTitle>
                        {idx === versions.length - 1 && !version.is_superseded && (
                          <Badge className="bg-blue-600 text-white text-xs">Current</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total</span>
                        <span className="font-mono font-bold">{formatCurrency(version.total_amount)}</span>
                      </div>
                      {version.amount_difference !== undefined && version.amount_difference !== 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Difference</span>
                          <AmountDifferenceBadge amount={version.amount_difference} />
                        </div>
                      )}
                      {version.regeneration_reason && (
                        <div className="pt-2 border-t">
                          <p className="text-muted-foreground text-xs">Regeneration Reason</p>
                          <p className="bg-orange-50 rounded p-2 text-xs text-orange-800">{version.regeneration_reason}</p>
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground pt-2 border-t">
                        Created: {formatDateTime(version.created_at)}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// REGENERATE DIALOG
// ═══════════════════════════════════════════════════════════════════════════════

const RegenerateInvoiceDialog = ({ invoice, open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setReason(invoice?.regeneration_reason || '');
    }
  }, [open, invoice]);

  const handleRegenerate = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/invoice-regen/invoices/${invoice.id}/regenerate`, {
        reason: reason || undefined,
      });
      toast.success(`Invoice regenerated as ${res.data.data?.invoice_number}`);
      onSuccess?.();
      onClose();
    } catch (e) {
      notify.error(e, 'INVOICE_REGEN_FAILED');
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="regenerate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Regenerate Invoice
          </DialogTitle>
          <DialogDescription>
            Create a new invoice version from latest approved timesheets
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert className="border-blue-200 bg-blue-50">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              This will create a new invoice and mark the current one as superseded.
              The original will be preserved for audit.
            </AlertDescription>
          </Alert>

          <Card className="bg-muted/50">
            <CardContent className="p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Invoice</span>
                <span className="font-mono">{invoice.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Participant</span>
                <span>{invoice.participant_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Current Total</span>
                <span className="font-mono font-bold">{formatCurrency(invoice.total_amount)}</span>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Label>Reason (Optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for regeneration..."
              rows={2}
              data-testid="regenerate-reason"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleRegenerate} disabled={loading} data-testid="confirm-regenerate">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
            Regenerate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// APPROVE ADJUSTMENT DIALOG
// ═══════════════════════════════════════════════════════════════════════════════

const ApproveAdjustmentDialog = ({ adjustment, open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');

  const handleApprove = async () => {
    setLoading(true);
    try {
      await api.post(`/invoice-regen/adjustments/${adjustment.id}/approve`, {
        approval_notes: notes || undefined,
      });
      toast.success('Adjustment approved');
      onSuccess?.();
      onClose();
    } catch (e) {
      notify.error(e, 'INVOICE_ADJUSTMENT_FAILED');
    } finally {
      setLoading(false);
    }
  };

  if (!adjustment) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="approve-adjustment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Approve Adjustment
          </DialogTitle>
          <DialogDescription>
            Confirm approval of this invoice adjustment
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reference</span>
                <span className="font-mono">{adjustment.adjustment_reference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Invoice</span>
                <span>{adjustment.original_invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type</span>
                <Badge className={cn(
                  "text-xs",
                  adjustment.adjustment_type === 'increase' ? "bg-green-600 text-white" : "bg-red-600 text-white"
                )}>
                  {adjustment.adjustment_type}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount</span>
                <AmountDifferenceBadge amount={adjustment.difference_amount} />
              </div>
              {adjustment.reason && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground">Reason</p>
                  <p className="text-xs">{adjustment.reason}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Label>Approval Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add approval notes..."
              rows={2}
              data-testid="approval-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleApprove} disabled={loading} className="bg-green-600 hover:bg-green-700" data-testid="confirm-approve">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function InvoiceRegenerationPage() {
  const { user, permissions } = useAuth();
  const [config, setConfig] = useState({ enabled: false });
  const [summary, setSummary] = useState({ needs_regeneration: 0, superseded_invoices: 0, pending_adjustments: 0 });
  const [needsRegenInvoices, setNeedsRegenInvoices] = useState([]);
  const [pendingAdjustments, setPendingAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('needs-regeneration');
  
  // Dialog states
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [versionInvoiceId, setVersionInvoiceId] = useState(null);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);

  const hasRegenPermission = permissions?.includes('invoice.regenerate') || 
    ['Super Admin', 'Admin'].includes(user?.role_name);
  const hasAdjustPermission = permissions?.includes('invoice.adjust') || 
    ['Super Admin', 'Admin', 'Accounts'].includes(user?.role_name);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/invoice-regen/config');
      setConfig(res.data);
    } catch {
      setConfig({ enabled: false });
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/invoice-regen/summary');
      setSummary(res.data.data || { needs_regeneration: 0, superseded_invoices: 0, pending_adjustments: 0 });
    } catch {
      // Ignore
    }
  }, []);

  const fetchNeedsRegen = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/invoice-regen/invoices/needs-regeneration');
      setNeedsRegenInvoices(res.data.items || []);
    } catch {
      notify.error(e, 'INVOICE_REGEN_LIST_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPendingAdjustments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/invoice-regen/adjustments/pending');
      setPendingAdjustments(res.data.items || []);
    } catch {
      notify.error(e, 'INVOICE_PENDING_ADJUSTMENTS_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchSummary();
  }, [fetchConfig, fetchSummary]);

  useEffect(() => {
    if (activeTab === 'needs-regeneration') {
      fetchNeedsRegen();
    } else if (activeTab === 'adjustments') {
      fetchPendingAdjustments();
    }
  }, [activeTab, fetchNeedsRegen, fetchPendingAdjustments]);

  const handleRegenerateClick = (invoice) => {
    setSelectedInvoice(invoice);
    setRegenerateOpen(true);
  };

  const handleViewVersions = (invoiceId) => {
    setVersionInvoiceId(invoiceId);
    setVersionHistoryOpen(true);
  };

  const handleApproveClick = (adj) => {
    setSelectedAdjustment(adj);
    setApproveOpen(true);
  };

  const handleSuccess = () => {
    fetchSummary();
    if (activeTab === 'needs-regeneration') {
      fetchNeedsRegen();
    } else if (activeTab === 'adjustments') {
      fetchPendingAdjustments();
    }
  };

  if (!config.enabled) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Invoice Regeneration Not Enabled</h2>
            <p className="text-muted-foreground">
              The invoice regeneration feature is not enabled. Contact your administrator.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoice-regeneration-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Invoice Regeneration & Adjustments</h1>
          <p className="text-muted-foreground mt-1">Manage invoice regeneration and financial adjustments</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchSummary(); handleSuccess(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="needs-regen-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Needs Regeneration</p>
                <p className="text-3xl font-bold mt-1">{summary.needs_regeneration}</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-100">
                <RotateCcw className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="pending-adj-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Adjustments</p>
                <p className="text-3xl font-bold mt-1">{summary.pending_adjustments}</p>
              </div>
              <div className="p-3 rounded-lg bg-yellow-100">
                <DollarSign className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="superseded-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Superseded Invoices</p>
                <p className="text-3xl font-bold mt-1">{summary.superseded_invoices}</p>
              </div>
              <div className="p-3 rounded-lg bg-gray-100">
                <FileText className="h-6 w-6 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Alert */}
      <Alert className="border-blue-200 bg-blue-50">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Invoice regeneration creates new versions. Original invoices are never modified.
          For submitted invoices, use the adjustment flow instead.
        </AlertDescription>
      </Alert>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="needs-regeneration" data-testid="needs-regen-tab">
            Needs Regeneration {summary.needs_regeneration > 0 && (
              <Badge className="ml-2 bg-orange-500 text-white">{summary.needs_regeneration}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="adjustments" data-testid="adjustments-tab">
            Pending Adjustments {summary.pending_adjustments > 0 && (
              <Badge className="ml-2 bg-yellow-500 text-white">{summary.pending_adjustments}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Needs Regeneration Tab */}
        <TabsContent value="needs-regeneration" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-orange-500" />
                Invoices Needing Regeneration
              </CardTitle>
              <CardDescription>
                Invoices marked for regeneration due to timesheet corrections
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : needsRegenInvoices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p>No invoices need regeneration</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Participant</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Triggered</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {needsRegenInvoices.map((inv) => (
                      <TableRow key={inv.id} data-testid={`regen-row-${inv.id}`}>
                        <TableCell className="font-mono">{inv.invoice_number}</TableCell>
                        <TableCell>{inv.participant_name}</TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          {formatCurrency(inv.total_amount)}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-xs">
                          {inv.regeneration_reason || '-'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDateTime(inv.regeneration_triggered_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewVersions(inv.id)}
                              data-testid={`view-versions-${inv.id}`}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            {hasRegenPermission && (
                              <Button
                                size="sm"
                                onClick={() => handleRegenerateClick(inv)}
                                data-testid={`regenerate-${inv.id}`}
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Regenerate
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Adjustments Tab */}
        <TabsContent value="adjustments" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-yellow-500" />
                Pending Adjustments
              </CardTitle>
              <CardDescription>
                Financial adjustments for submitted invoices awaiting approval
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : pendingAdjustments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p>No pending adjustments</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reference</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Participant</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Difference</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingAdjustments.map((adj) => (
                      <TableRow key={adj.id} data-testid={`adj-row-${adj.id}`}>
                        <TableCell className="font-mono">{adj.adjustment_reference}</TableCell>
                        <TableCell>{adj.original_invoice_number}</TableCell>
                        <TableCell>{adj.participant_name}</TableCell>
                        <TableCell>
                          <Badge className={cn(
                            "text-xs",
                            adj.adjustment_type === 'increase' ? "bg-green-600 text-white" : "bg-red-600 text-white"
                          )}>
                            {adj.adjustment_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <AmountDifferenceBadge amount={adj.difference_amount} />
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-xs">
                          {adj.reason || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {hasAdjustPermission && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApproveClick(adj)}
                              data-testid={`approve-${adj.id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <RegenerateInvoiceDialog
        invoice={selectedInvoice}
        open={regenerateOpen}
        onClose={() => setRegenerateOpen(false)}
        onSuccess={handleSuccess}
      />

      <ApproveAdjustmentDialog
        adjustment={selectedAdjustment}
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        onSuccess={handleSuccess}
      />

      <InvoiceVersionHistoryDrawer
        invoiceId={versionInvoiceId}
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
      />
    </div>
  );
}
